import React, { useState, useEffect, useRef } from 'react';
import './MeasuringTool.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRulerCombined } from '@fortawesome/free-solid-svg-icons';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import * as turf from '@turf/turf';
import MeasurementDisplay from './MeasurementDisplay';
import { initializeMapLayers, handleClick, MEASURE_POINTS_LAYER_ID, MEASURE_LINES_LAYER_ID, MEASURE_POLYGONS_LAYER_ID, MEASURE_LABELS_LAYER_ID } from './measurementUtilities';
import { logger } from '../../../utils/logger';

const log = logger('MeasuringTool');

interface MeasuringToolProps {
  map: mapboxgl.Map;
}

const MeasuringTool: React.FC<MeasuringToolProps> = ({ map }) => {
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurements, setMeasurements] = useState({ distanceKm: 0, distanceNm: 0, areaKm2: 0, areaM2: 0, areaHa: 0 });
  const [drawnFeatures, setDrawnFeatures] = useState<GeoJSON.Feature<GeoJSON.Point>[]>([]);
  const [segmentLabels, setSegmentLabels] = useState<GeoJSON.Feature<GeoJSON.Point>[]>([]);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const cursorPopupRef = useRef<mapboxgl.Popup | null>(null);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const trashLastSegment = () => {
    if (drawnFeatures.length > 0) {
      const newDrawnFeatures = drawnFeatures.slice(0, -1);
      setDrawnFeatures(newDrawnFeatures);

      const newSegmentLabels = segmentLabels.slice(0, -1);
      setSegmentLabels(newSegmentLabels);

      const featureCollection: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [...newDrawnFeatures]
      };

      if (newDrawnFeatures.length > 1) {
        const line: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: newDrawnFeatures.map(feature => (feature.geometry as GeoJSON.Point).coordinates)
          },
          properties: {}
        };

        featureCollection.features.push(line);
      }

      if (newDrawnFeatures.length > 2) {
        const polygon: GeoJSON.Feature<GeoJSON.Polygon> = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[...newDrawnFeatures.map(feature => (feature.geometry as GeoJSON.Point).coordinates), (newDrawnFeatures[0].geometry as GeoJSON.Point).coordinates]]
          },
          properties: {}
        };

        featureCollection.features.push(polygon);

        const areaM2 = turf.area(polygon);
        const areaKm2 = areaM2 / 1000000;
        const areaHa = areaKm2 * 100;
        setMeasurements(prev => ({ ...prev, areaKm2, areaM2, areaHa }));
      } else {
        setMeasurements({ distanceKm: 0, distanceNm: 0, areaKm2: 0, areaM2: 0, areaHa: 0 });
      }

      (map.getSource(MEASURE_POINTS_LAYER_ID) as mapboxgl.GeoJSONSource).setData(featureCollection);

      const measureLabelsSource = map.getSource(MEASURE_LABELS_LAYER_ID) as mapboxgl.GeoJSONSource;
      if (measureLabelsSource) {
        measureLabelsSource.setData({
          type: 'FeatureCollection',
          features: newSegmentLabels
        });
      }
    }
  };

  const trashAllSegments = () => {
    setDrawnFeatures([]);
    setSegmentLabels([]);
    setMeasurements({ distanceKm: 0, distanceNm: 0, areaKm2: 0, areaM2: 0, areaHa: 0 });

    // Clear the source data
    if (map.getSource(MEASURE_POINTS_LAYER_ID)) {
      (map.getSource(MEASURE_POINTS_LAYER_ID) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: []
      });
    }
    if (map.getSource(MEASURE_LABELS_LAYER_ID)) {
      (map.getSource(MEASURE_LABELS_LAYER_ID) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  };

  useEffect(() => {
    if (isMeasuring && map) {
      // log.debug('Measuring tool enabled');

      // Disable map panning and change cursor
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      // map.dragPan.disable();
      map.keyboard.disable();
      map.scrollZoom.disable();
      map.touchZoomRotate.disable();
      map.getCanvas().style.cursor = 'crosshair';

      // Disable default touch behaviors on mobile
      const disableTouchBehaviors = (e: TouchEvent) => {
        e.preventDefault();
      };
      document.addEventListener('touchmove', disableTouchBehaviors, { passive: false });

      // Initialize map layers and sources
      initializeMapLayers(map);

      const handleClickWrapper = (e: MapMouseEvent) => handleClick(e, map, drawnFeatures, setDrawnFeatures, segmentLabels, setSegmentLabels, setMeasurements);

      // Live cursor tooltip showing distance/bearing from last placed point
      if (!cursorPopupRef.current) {
        cursorPopupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'measure-cursor-popup',
          offset: [15, 0],
          anchor: 'left'
        });
      }

      const handleMouseMove = (e: MapMouseEvent) => {
        if (drawnFeatures.length === 0) {
          cursorPopupRef.current?.remove();
          return;
        }

        const lastPoint = drawnFeatures[drawnFeatures.length - 1].geometry.coordinates as [number, number];
        const cursor: [number, number] = [e.lngLat.lng, e.lngLat.lat];

        const distKm = turf.distance(lastPoint, cursor, { units: 'kilometers' });
        const distNm = distKm * 0.539957;
        let bearing = turf.bearing(lastPoint, cursor);
        if (bearing < 0) bearing += 360;

        cursorPopupRef.current!
          .setLngLat(e.lngLat)
          .setHTML(`<strong>${distKm.toFixed(2)} km / ${distNm.toFixed(2)} nm</strong><br/>Bearing: ${bearing.toFixed(1)}°`)
          .addTo(map);
      };

      map.on('click', handleClickWrapper);
      map.on('mousemove', handleMouseMove);

      return () => {
        map.off('click', handleClickWrapper);
        map.off('mousemove', handleMouseMove);
        cursorPopupRef.current?.remove();

        // Re-enable map panning and reset cursor
        map.boxZoom.enable();
        map.doubleClickZoom.enable();
        map.dragPan.enable();
        map.keyboard.enable();
        map.scrollZoom.enable();
        map.touchZoomRotate.enable();
        map.getCanvas().style.cursor = '';
      };
    } else if (!isMeasuring && map) {
      log.debug('Measuring tool disabled');

      // Re-enable map panning and reset cursor
      map.boxZoom.enable();
      map.doubleClickZoom.enable();
      // map.dragPan.enable();
      map.keyboard.enable();
      map.scrollZoom.enable();
      map.touchZoomRotate.enable();
      map.getCanvas().style.cursor = '';
    }
  }, [isMeasuring, map, drawnFeatures, segmentLabels]);

  const startMeasuring = () => {
    log.debug('Starting measuring');
    setIsMeasuring(true);
  };

  const stopMeasuring = () => {
    log.debug('Stopping measuring');
    cursorPopupRef.current?.remove();
    setIsMeasuring(false);
    setDrawnFeatures([]);
    setSegmentLabels([]);
    setMeasurements({ distanceKm: 0, distanceNm: 0, areaKm2: 0, areaM2: 0, areaHa: 0 });

    // Clear the source data
    if (map.getSource(MEASURE_POINTS_LAYER_ID)) {
      (map.getSource(MEASURE_POINTS_LAYER_ID) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: []
      });
    }
    if (map.getSource(MEASURE_LABELS_LAYER_ID)) {
      (map.getSource(MEASURE_LABELS_LAYER_ID) as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  };

  return (
    <div className="measuring-tool">
      <button className={`measure-button control-button${isMeasuring ? ' active' : ''}`} title='Measure distance, bearing and area' onClick={isMeasuring ? stopMeasuring : startMeasuring}>
        <FontAwesomeIcon icon={faRulerCombined} />
      </button>
      {isMeasuring && (
        <MeasurementDisplay
          measurements={measurements}
          isCollapsed={isCollapsed}
          toggleCollapse={toggleCollapse}
          trashLastSegment={trashLastSegment}
          trashAllSegments={trashAllSegments}
          segmentLabels={segmentLabels}
        />
      )}
    </div>
  );
};

export default MeasuringTool;